import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../utils/api';
import {
  ShoppingCart,
  AlertTriangle,
  RefreshCw,
  Store,
} from 'lucide-react';

interface DashboardStats {
  todayOrders: number;
  lowStockCount: number;
  activeShops: number;
  pendingSync: number;
}

interface RecentOrder {
  id: string;
  mallOrderId: string;
  shopName: string;
  customerName: string;
  totalAmount: number;
  orderStatus: string;
}

interface StockAlert {
  sku: string;
  name: string;
  stock: number;
  safetyStock: number;
}

const statusLabels: Record<string, { label: string; color: string }> = {
  new: { label: '新規', color: 'bg-blue-100 text-blue-700' },
  confirmed: { label: '確認済', color: 'bg-green-100 text-green-700' },
  printed: { label: '印刷済', color: 'bg-yellow-100 text-yellow-700' },
  shipped: { label: '発送済', color: 'bg-purple-100 text-purple-700' },
  cancelled: { label: 'キャンセル', color: 'bg-red-100 text-red-700' },
  returned: { label: '返品', color: 'bg-gray-100 text-gray-700' },
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    todayOrders: 0,
    lowStockCount: 0,
    activeShops: 0,
    pendingSync: 0,
  });
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [stockAlerts, setStockAlerts] = useState<StockAlert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  async function fetchDashboardData() {
    setLoading(true);
    try {
      // 受注統計を取得
      const orderStatsRes = await apiFetch('/api/orders/stats');
      const orderStatsData = await orderStatsRes.json();
      const orderStats = orderStatsData.stats || {};

      // 最近の受注を取得
      const ordersRes = await apiFetch('/api/orders?limit=5');
      const ordersData = await ordersRes.json();

      // 店舗一覧を取得
      const shopsRes = await apiFetch('/api/shops');
      const shopsData = await shopsRes.json();

      // 在庫サマリーを取得
      const invSummaryRes = await apiFetch('/api/inventory/summary');
      const invSummaryData = await invSummaryRes.json();

      // 同期キューを取得
      const syncQueueRes = await apiFetch('/api/inventory/sync-queue?status=pending&limit=100');
      const syncQueueData = await syncQueueRes.json();

      // 在庫アラートを取得
      const invRes = await apiFetch('/api/inventory?lowStock=true&limit=5');
      const invData = await invRes.json();

      setStats({
        todayOrders: (orderStats.new || 0) + (orderStats.confirmed || 0),
        lowStockCount: invSummaryData.summary?.lowStockCount || 0,
        activeShops: shopsData.shops?.filter((s: any) => s.isActive).length || 0,
        pendingSync: syncQueueData.queue?.length || 0,
      });

      setRecentOrders(
        (ordersData.orders || []).map((o: any) => ({
          id: o.id,
          mallOrderId: o.mallOrderId,
          shopName: o.shop?.shopName || '-',
          customerName: o.customerInfo?.name || '-',
          totalAmount: parseFloat(o.totalAmount) || 0,
          orderStatus: o.orderStatus,
        }))
      );

      setStockAlerts(
        (invData.inventory || []).map((inv: any) => ({
          sku: inv.variant?.variantCode || inv.variantId?.substring(0, 8),
          name: inv.product?.productName || '-',
          stock: inv.freeStock,
          safetyStock: inv.safetyStock,
        }))
      );
    } catch (err) {
      console.error('Dashboard data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }

  const statCards = [
    {
      label: '未処理の受注',
      value: stats.todayOrders,
      icon: ShoppingCart,
      color: 'bg-blue-500',
      link: '/orders',
    },
    {
      label: '在庫アラート',
      value: stats.lowStockCount,
      icon: AlertTriangle,
      color: 'bg-orange-500',
      link: '/inventory',
    },
    {
      label: '連携店舗',
      value: stats.activeShops,
      icon: Store,
      color: 'bg-purple-500',
      link: '/shops',
    },
    {
      label: '同期待ち',
      value: stats.pendingSync,
      icon: RefreshCw,
      color: 'bg-green-500',
      link: '/sync',
    },
  ];

  return (
    <div className="space-y-6">
      {/* 挨拶 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            こんにちは、{user?.name}さん
          </h1>
          <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>
            在庫連動システムへようこそ。今日も効率的な運用を。
          </p>
        </div>
        <button onClick={fetchDashboardData} className="btn-secondary flex items-center gap-2">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          更新
        </button>
      </div>

      {/* 統計カード */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Link
            key={stat.label}
            to={stat.link}
            className="card p-5 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}
              >
                <stat.icon size={20} className="text-white" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {stat.value}
              </p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {stat.label}
              </p>
            </div>
          </Link>
        ))}
      </div>

      {/* メインコンテンツ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 最近の受注 */}
        <div className="lg:col-span-2 card">
          <div className="p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <div className="flex items-center justify-between">
              <h2 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                最近の受注
              </h2>
              <Link
                to="/orders"
                className="text-sm hover:underline"
                style={{ color: 'var(--accent-color)' }}
              >
                すべて表示
              </Link>
            </div>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : recentOrders.length === 0 ? (
              <div className="p-8 text-center" style={{ color: 'var(--text-secondary)' }}>
                受注データがありません
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                      受注番号
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                      店舗
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                      顧客名
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                      金額
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                      ステータス
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                  {recentOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-4 py-3 text-sm font-mono" style={{ color: 'var(--text-primary)' }}>
                        {order.mallOrderId}
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-primary)' }}>
                        {order.shopName}
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-primary)' }}>
                        {order.customerName}
                      </td>
                      <td className="px-4 py-3 text-sm text-right" style={{ color: 'var(--text-primary)' }}>
                        ¥{order.totalAmount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusLabels[order.orderStatus]?.color}`}
                        >
                          {statusLabels[order.orderStatus]?.label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* 在庫アラート */}
        <div className="card">
          <div className="p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <div className="flex items-center justify-between">
              <h2 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                在庫アラート
              </h2>
              <Link
                to="/inventory"
                className="text-sm hover:underline"
                style={{ color: 'var(--accent-color)' }}
              >
                すべて表示
              </Link>
            </div>
          </div>
          <div className="p-4 space-y-4">
            {loading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : stockAlerts.length === 0 ? (
              <div className="text-center py-4" style={{ color: 'var(--text-secondary)' }}>
                在庫アラートはありません
              </div>
            ) : (
              stockAlerts.map((item) => (
                <div
                  key={item.sku}
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: 'var(--light-gray-primary)' }}
                >
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {item.name}
                    </p>
                    <p className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>
                      {item.sku}
                    </p>
                  </div>
                  <div className="text-right">
                    <p
                      className={`text-lg font-bold ${
                        item.stock === 0 ? 'text-red-600' : 'text-orange-600'
                      }`}
                    >
                      {item.stock}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      / {item.safetyStock}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
